/* oxlint-disable react/jsx-no-useless-fragment */
import { useState } from 'react';
import { FormattedMessage } from 'react-intl';
import cx from 'clsx';
import { Button } from '@repo/design-system-kit/components/button/button';
import { ResponsiveCta } from '@repo/shared-kit/components/responsive-cta/responsive-cta';
import { Disclaimer } from '@repo/design-system-kit/components/disclaimer/disclaimer';
import { ConsentCheckbox } from '@repo/shared-kit/marketing-consent/components/consent-checkbox/consent-checkbox';
import { TNCLink } from '@repo/shared-kit/components/tnc-link/tnc-link';
import IconArrowLeftOutlined from '@repo/monochrome-icons/icon-arrow-left-outlined';
import IconCheckmarkOutlined from '@repo/monochrome-icons/icon-checkmark-outlined';
import styles from './consent-page.module.css';

export interface ConsentPageProps {
  onContinue: (hasConsent: boolean) => void;
  onBack?: () => void;
  className?: string;
  layout?: 'control' | 'treatment';
}

export function ConsentPage({
  onContinue,
  onBack,
  className,
  layout = 'treatment',
}: ConsentPageProps): React.ReactElement {
  const [hasConsent, setHasConsent] = useState<boolean>(false);
  const isEnhancedLayout = layout === 'treatment';

  const handleConsentChange = (isChecked: boolean): void => {
    setHasConsent(isChecked);
  };

  const handleContinue = (): void => {
    onContinue(hasConsent);
  };

  const handleBack = (): void => {
    onBack?.();
  };

  return (
    <div className={cx(styles.content, className)}>
      {onBack ? (
        <div className={styles.backButtonContainer}>
          <Button
            iconLeft={<IconArrowLeftOutlined />}
            onPress={handleBack}
            size="small"
            type="button"
            variant="tertiary"
          >
            <FormattedMessage id="shared-kit.btn.back" />
          </Button>
        </div>
      ) : null}

      <h1 className="heading-section mb-32">
        <FormattedMessage id="customer-registration-kit.sign_up.gdpr-consent.title" />
      </h1>

      {isEnhancedLayout ? (
        <>
          <p className={cx('body-standard', styles.subtitle)}>
            <FormattedMessage id="customer-registration-kit.sign_up.gdpr-consent.subtitle" />
          </p>

          <div className={styles.benefitsCard}>
            <ul className={styles.benefitsList}>
              <li className={styles.benefitItem}>
                <span className={styles.checkmarkIcon}>
                  <IconCheckmarkOutlined />
                </span>
                <FormattedMessage id="customer-registration-kit.sign_up.gdpr-consent.product-updates.label" />
              </li>
              <li className={styles.benefitItem}>
                <span className={styles.checkmarkIcon}>
                  <IconCheckmarkOutlined />
                </span>
                <FormattedMessage id="customer-registration-kit.sign_up.gdpr-consent.insights.label" />
              </li>
              <li className={styles.benefitItem}>
                <span className={styles.checkmarkIcon}>
                  <IconCheckmarkOutlined />
                </span>
                <FormattedMessage id="customer-registration-kit.sign_up.gdpr-consent.special-and-partner-offers.label" />
              </li>
            </ul>
          </div>

          <div className={styles.disclaimerSection}>
            <Disclaimer.Inline level="info">
              <FormattedMessage
                id="customer-registration-kit.sign_up.gdpr-consent.disclaimer.text"
                values={{
                  link: (
                    <TNCLink
                      className={cx('body-standard-link', styles.disclaimerLink)}
                      language="en"
                      tncKey="qonto-privacy-policy"
                    >
                      <FormattedMessage id="customer-registration-kit.sign_up.gdpr-consent.disclaimer.link" />
                    </TNCLink>
                  ),
                }}
              />
            </Disclaimer.Inline>
          </div>

          <div className={styles.treatmentConsentSection}>
            <ConsentCheckbox onChange={handleConsentChange}>
              <FormattedMessage id="customer-registration-kit.sign_up.gdpr-consent.receive-updates.checkbox" />
            </ConsentCheckbox>
          </div>
        </>
      ) : (
        <>
          <div className={styles.consentSection}>
            <ConsentCheckbox onChange={handleConsentChange}>
              <FormattedMessage id="customer-registration-kit.sign_up.gdpr-consent.caption" />
            </ConsentCheckbox>
          </div>
        </>
      )}

      <div>
        <ResponsiveCta
          className="mb-16"
          data-test-consent-continue
          onPress={handleContinue}
          size="medium"
        >
          <FormattedMessage id="customer-registration-kit.sign_up.gdpr-consent.continue" />
        </ResponsiveCta>
      </div>

      {!isEnhancedLayout && (
        <div className={styles.additionalInfo}>
          <span className="caption">
            <FormattedMessage id="customer-registration-kit.sign_up.gdpr-consent.footer-text" />
            <TNCLink className="caption-link" language="en" tncKey="qonto-privacy-policy">
              <FormattedMessage id="customer-registration-kit.sign_up.gdpr-consent.link-text" />
            </TNCLink>
            <FormattedMessage id="customer-registration-kit.sign_up.gdpr-consent.footer-end" />
          </span>
        </div>
      )}
    </div>
  );
}
